import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { FileText, CheckCircle, XCircle, Clock, User } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

interface TrainerApplication {
  id: string;
  user_id: string;
  status: 'pending' | 'approved' | 'rejected';
  bio: string | null;
  experience_level: string | null;
  years_of_experience: number | null;
  hourly_rate: number | null;
  specialties: string[] | null;
  submitted_at: string;
  reviewed_at: string | null;
  rejection_reason: string | null;
  user_name?: string;
  user_email?: string;
}

export default function AdminDashboardPage() {
  const { profile } = useAuth();
  const [applications, setApplications] = useState<TrainerApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
  });

  const loadApplications = useCallback(async () => {
    if (!profile || profile.role !== 'admin') return;

    try {
      const { data, error } = await supabase
        .from('trainer_applications')
        .select('*')
        .order('submitted_at', { ascending: false });

      if (error) throw error;

      // Fetch user names and emails
      const enrichedApplications = await Promise.all(
        (data || []).map(async (app) => {
          const { data: userProfile } = await supabase
            .from('profiles')
            .select('full_name, email')
            .eq('id', app.user_id)
            .single();

          return {
            ...app,
            user_name: userProfile?.full_name || 'Unknown',
            user_email: userProfile?.email || '',
          };
        })
      );

      setApplications(enrichedApplications as TrainerApplication[]);

      // Calculate stats
      const total = data?.length || 0;
      const pending = data?.filter((a) => a.status === 'pending').length || 0;
      const approved = data?.filter((a) => a.status === 'approved').length || 0;
      const rejected = data?.filter((a) => a.status === 'rejected').length || 0;

      setStats({ total, pending, approved, rejected });
    } catch (error) {
      console.error('Error loading applications:', error);
    } finally {
      setLoading(false);
    }
  }, [profile]);

  useEffect(() => {
    if (profile) {
      loadApplications();
    }
  }, [profile, loadApplications]);

  const handleApprove = async (applicationId: string, userId: string) => {
    if (!profile || profile.role !== 'admin') return;

    try {
      // Update application status
      const { error: updateError } = await supabase
        .from('trainer_applications')
        .update({
          status: 'approved',
          reviewed_at: new Date().toISOString(),
          reviewed_by: profile.id,
        })
        .eq('id', applicationId);

      if (updateError) throw updateError;

      // Update user role to trainer
      const { error: roleError } = await supabase
        .from('profiles')
        .update({ role: 'trainer' })
        .eq('id', userId);

      if (roleError) throw roleError;

      // Get application data to create trainer record
      const application = applications.find((a) => a.id === applicationId);
      if (application) {
        const { data: userProfile } = await supabase
          .from('profiles')
          .select('full_name, email')
          .eq('id', userId)
          .single();

        // Create trainer record
        const { error: trainerError } = await supabase
          .from('trainers')
          .insert({
            profile_id: userId,
            name: userProfile?.full_name || 'Trainer',
            email: userProfile?.email || '',
            bio: application.bio || '',
            hourly_rate: application.hourly_rate || 0,
            experience_level: application.experience_level || 'beginner',
            verification_status: 'verified',
            is_active: true,
          });

        if (trainerError) {
          console.error('Error creating trainer record:', trainerError);
        }
      }

      await loadApplications();
    } catch (error) {
      console.error('Error approving application:', error);
      alert('Failed to approve application. Please try again.');
    }
  };

  const handleReject = async (applicationId: string, reason: string) => {
    if (!profile || profile.role !== 'admin') return;

    try {
      const { error } = await supabase
        .from('trainer_applications')
        .update({
          status: 'rejected',
          reviewed_at: new Date().toISOString(),
          reviewed_by: profile.id,
          rejection_reason: reason,
        })
        .eq('id', applicationId);

      if (error) throw error;

      await loadApplications();
    } catch (error) {
      console.error('Error rejecting application:', error);
      alert('Failed to reject application. Please try again.');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="flex min-h-screen items-center justify-center">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
            <p className="mt-4 text-sm text-gray-600">Loading...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="min-h-screen bg-gray-50 py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="mt-2 text-gray-600">
              Manage trainer applications and system overview
            </p>
          </div>

          {/* Stats Grid */}
          <div className="mb-8 grid gap-4 sm:grid-cols-4">
            <div className="rounded-3xl border border-blue-100 bg-white/90 p-5 shadow-sm">
              <p className="text-sm font-semibold text-gray-700">Total Applications</p>
              <p className="text-3xl font-bold mt-2 flex items-center gap-2">
                <FileText className="h-6 w-6 text-blue-600" />
                {stats.total}
              </p>
            </div>
            <div className="rounded-3xl border border-yellow-100 bg-white/90 p-5 shadow-sm">
              <p className="text-sm font-semibold text-gray-700">Pending</p>
              <p className="text-3xl font-bold mt-2 flex items-center gap-2">
                <Clock className="h-6 w-6 text-yellow-600" />
                {stats.pending}
              </p>
            </div>
            <div className="rounded-3xl border border-green-100 bg-white/90 p-5 shadow-sm">
              <p className="text-sm font-semibold text-gray-700">Approved</p>
              <p className="text-3xl font-bold mt-2 flex items-center gap-2">
                <CheckCircle className="h-6 w-6 text-green-600" />
                {stats.approved}
              </p>
            </div>
            <div className="rounded-3xl border border-red-100 bg-white/90 p-5 shadow-sm">
              <p className="text-sm font-semibold text-gray-700">Rejected</p>
              <p className="text-3xl font-bold mt-2 flex items-center gap-2">
                <XCircle className="h-6 w-6 text-red-600" />
                {stats.rejected}
              </p>
            </div>
          </div>

          {/* Applications List */}
          <section>
            <h2 className="mb-4 text-2xl font-bold text-gray-900">Trainer Applications</h2>
            {applications.length === 0 ? (
              <div className="rounded-xl border border-gray-200 bg-white p-12 text-center">
                <FileText className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-4 text-lg font-semibold text-gray-900">
                  No applications found
                </h3>
                <p className="mt-2 text-sm text-gray-600">
                  Trainer applications will appear here when submitted.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {applications.map((application) => (
                  <div
                    key={application.id}
                    className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <User className="h-5 w-5 text-gray-400" />
                          <h3 className="text-lg font-semibold text-gray-900">
                            {application.user_name}
                          </h3>
                          <span
                            className={`rounded-full px-2 py-1 text-xs font-semibold ${
                              application.status === 'approved'
                                ? 'bg-green-100 text-green-700'
                                : application.status === 'rejected'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-yellow-100 text-yellow-700'
                            }`}
                          >
                            {application.status}
                          </span>
                        </div>
                        <p className="mt-1 text-sm text-gray-600">{application.user_email}</p>
                        <div className="mt-4 space-y-2 text-sm text-gray-600">
                          {application.bio && (
                            <p>
                              <span className="font-semibold">Bio:</span> {application.bio}
                            </p>
                          )}
                          {application.experience_level && (
                            <p>
                              <span className="font-semibold">Experience:</span>{' '}
                              {application.experience_level}
                              {application.years_of_experience &&
                                ` (${application.years_of_experience} years)`}
                            </p>
                          )}
                          {application.hourly_rate && (
                            <p>
                              <span className="font-semibold">Hourly Rate:</span> $
                              {application.hourly_rate.toFixed(2)}
                            </p>
                          )}
                          {application.specialties && application.specialties.length > 0 && (
                            <p>
                              <span className="font-semibold">Specialties:</span>{' '}
                              {application.specialties.join(', ')}
                            </p>
                          )}
                          <p>
                            <span className="font-semibold">Submitted:</span>{' '}
                            {formatDate(application.submitted_at)}
                          </p>
                          {application.rejection_reason && (
                            <p className="text-red-600">
                              <span className="font-semibold">Rejection Reason:</span>{' '}
                              {application.rejection_reason}
                            </p>
                          )}
                        </div>
                      </div>
                      {application.status === 'pending' && (
                        <div className="ml-4 flex gap-2">
                          <button
                            onClick={() => handleApprove(application.id, application.user_id)}
                            className="rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => {
                              const reason = prompt('Enter rejection reason:');
                              if (reason) {
                                handleReject(application.id, reason);
                              }
                            }}
                            className="rounded-lg border border-red-300 bg-white px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50"
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}
